<template>
  <b-modal
    id="md-warranty-change-status"
    v-model="visible"
    hide-footer
    title="Đổi trạng thái phiếu bảo hành"
    no-close-on-backdrop
    size="lg"
    @hidden="onHidden"
  >
    <div class="mt-4">
      <b-row>
        <b-col cols="4">
          <b-form-group
            label="Chọn trạng thái"
            label-class="font-weight-bold"
            class="mb-0"
          >
            <b-form-select
              v-model="globalStatus"
              :options="statusOptions"
              class="select-style"
              value-field="id"
              text-field="name"
              @change="onGlobalStatusChange"
            >
              <template #first>
                <b-form-select-option :value="null">--Chọn trạng thái--</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <b-table
        class="mt-3 table-common"
        hover
        bordered
        :items="localItems"
        :fields="modalFields"
      >
        <template #cell(id)="data">
          <span class="font-weight-bold">#{{ data.item.id }}</span>
        </template>
        <template #cell(statusChange)="data">
          <div class="d-flex align-items-center flex-nowrap">
            <div style="min-width: 100px">
              <WarrantyStatusBadge
                :status="data.item.status"
                :status-name="data.item.statusName"
              />
            </div>
            <i
              class="fa fa-arrow-right mx-2 text-primary"
              aria-hidden="true"
            />
            <b-form-select
              v-model="data.item.newStatus"
              :options="statusOptions"
              :disabled="isReturnedItem(data.item)"
              class="select-style"
              value-field="id"
              text-field="name"
              size="sm"
            >
              <template #first>
                <b-form-select-option :value="null">--Chọn trạng thái--</b-form-select-option>
              </template>
            </b-form-select>
          </div>
        </template>
        <template #cell(isError)="data">
          <div v-if="data.item.isError === true" class="d-flex align-items-center">
            <i
              class="fas fa-times-circle text-danger mr-2"
              v-b-tooltip.hover
              :title="data.item.message || 'Cập nhật thất bại'"
            ></i>
            <span class="text-danger small">
              {{ data.item.message || 'Không hợp lệ' }}
            </span>
          </div>
          <div v-else-if="data.item.isError === false" class="d-flex align-items-center">
            <i
              class="fas fa-check-circle text-success mr-2"
              v-b-tooltip.hover
              title="Cập nhật thành công"
            ></i>
            <span class="text-success small">Hợp lệ</span>
          </div>
          <span v-else class="text-muted small">—</span>
        </template>
      </b-table>
    </div>
    <div class="mt-3">
      <b-button
        :disabled="isBusy || !localItems.length || !hasSelectedNewStatus"
        variant="primary"
        size="sm"
        @click="onSave"
      >
        <b-spinner v-if="isBusy" small class="mr-1" />
        <strong>Cập nhật</strong>
      </b-button>
      <b-button variant="secondary" size="sm" class="ml-2" @click="hide">
        <strong>Hủy</strong>
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { bulkUpdateWarrantyStatus } from '../utils/warrantyApi';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import WarrantyStatusBadge from './WarrantyStatusBadge.vue';
import { WARRANTY_STATUS_OPTIONS } from '@/utils/constants';
import { WARRANTY_RECEPTION_STATUS } from '@/utils/enum';

export default {
  name: 'WarrantyChangeStatusModal',
  components: { WarrantyStatusBadge },
  props: {
    selectedItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      visible: false,
      globalStatus: null,
      isBusy: false,
      localItems: [],
      modalFields: [
        { key: 'id', label: 'ID', thClass: 'text-center', tdClass: 'align-middle', thStyle: { width: '10%' } },
        { key: 'imeiCode', label: 'IMEI', thClass: 'text-center', tdClass: 'align-middle', thStyle: { width: '20%' } },
        { key: 'statusChange', label: 'Đổi trạng thái', tdClass: 'align-middle', thStyle: { width: '40%' } },
        { key: 'isError', label: 'Kiểm tra', tdClass: 'align-middle', thStyle: { width: '30%' } },
      ],
    };
  },
  computed: {
    statusOptions() {
      return WARRANTY_STATUS_OPTIONS;
    },
    hasSelectedNewStatus() {
      return this.localItems.some((item) => item.newStatus && !this.isReturnedItem(item));
    },
  },
  methods: {
    onGlobalStatusChange(val) {
      this.localItems.forEach((item) => {
        item.newStatus = val;
      });
    },
    isReturnedItem(item) {
      return item.status === WARRANTY_RECEPTION_STATUS.RETURNED;
    },
    show() {
      this.globalStatus = null;
      this.localItems = this.selectedItems.map((item) => ({
        ...item,
        newStatus: null,
        isError: this.isReturnedItem(item) ? true : null,
        message: this.isReturnedItem(item) ? 'Không được cập nhật trạng thái phiếu bảo hành Đã trả' : '',
      }));
      this.visible = true;
    },
    hide() {
      this.visible = false;
    },
    onHidden() {
      this.$emit('hidden');
    },
    async onSave() {
      if (!this.hasSelectedNewStatus || !this.localItems.length || this.isBusy) return;

      this.isBusy = true;
      // Reset kết quả kiểm tra của lần trước, giữ nguyên lỗi của phiếu Đã trả
      this.localItems = this.localItems.map((item) => {
        if (this.isReturnedItem(item)) return item;
        return { ...item, isError: null, message: '' };
      });

      try {
        const payload = {
          warranties: this.localItems
            .filter((item) => item.newStatus && item.newStatus !== item.status && !this.isReturnedItem(item))
            .map((item) => ({
              id: item.id,
              status: item.newStatus,
            })),
        };

        const results = await bulkUpdateWarrantyStatus(payload);

        const mergedItems = this.localItems.map((item) => {
          if (!item.newStatus || this.isReturnedItem(item)) return item;

          const result = Array.isArray(results)
            ? results.find((r) => r.id === item.id)
            : null;

          if (!result) {
            return {
              ...item,
              isError: true,
              message: 'Không nhận được kết quả từ máy chủ',
            };
          }

          return {
            ...item,
            isError: !!result.isError,
            message: result.message || '',
          };
        });

        this.localItems = mergedItems;

        const hasError = mergedItems.some((item) => item.isError);

        if (!hasError) {
          makeToastSuccess('Cập nhật trạng thái thành công');
          this.hide();
          this.$emit('refresh');
        } else {
          makeToastFaile('Một số phiếu bảo hành cập nhật thất bại');
        }
      } catch (err) {
        makeToastFaile('Không thể cập nhật trạng thái phiếu bảo hành');
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>